import type { Carer } from '@lmc/ts-common'
import { UserRole } from '@lmc/ts-common'

export enum RoleTabId {
  AllMembers = 'all-members',
  GroupAdmin = 'group-admin',
  GroupManager = 'group-manager',
  Admin = 'admin',
  Management = 'management',
  SeniorCarer = 'senior-carer',
  Carer = 'carer',
  ItSecurity = 'it-security',
  AuditView = 'audit-view'
}

// The single-site team list shows seven tabs. The group screen opts in to the
// two extra group-scoped tabs (Group Admin / Group Manager) via
// `includeGroupRoles` on `RoleTabs`.
const GROUP_ROLE_TAB_IDS: readonly RoleTabId[] = [
  RoleTabId.GroupAdmin,
  RoleTabId.GroupManager
]

export const ROLE_TAB_IDS: readonly RoleTabId[] = [
  RoleTabId.AllMembers,
  RoleTabId.Admin,
  RoleTabId.Management,
  RoleTabId.SeniorCarer,
  RoleTabId.Carer,
  RoleTabId.ItSecurity,
  RoleTabId.AuditView
]

// Ordered to match Figma 8363-41901: All members · Group Admin · Group Manager
// · Admin · Management · Senior Carer · Carer · IT & Security · Audit View.
export const GROUP_ROLE_TAB_IDS_ORDERED: readonly RoleTabId[] = [
  RoleTabId.AllMembers,
  RoleTabId.GroupAdmin,
  RoleTabId.GroupManager,
  RoleTabId.Admin,
  RoleTabId.Management,
  RoleTabId.SeniorCarer,
  RoleTabId.Carer,
  RoleTabId.ItSecurity,
  RoleTabId.AuditView
]

const ROLE_TAB_TO_USER_ROLE: Record<
  Exclude<RoleTabId, RoleTabId.AllMembers>,
  string
> = {
  [RoleTabId.GroupAdmin]: UserRole.groupAdmin,
  [RoleTabId.GroupManager]: UserRole.groupManager,
  [RoleTabId.Admin]: UserRole.careHomeAdmin,
  [RoleTabId.Management]: UserRole.management,
  [RoleTabId.SeniorCarer]: UserRole.seniorCarer,
  [RoleTabId.Carer]: UserRole.carer,
  [RoleTabId.ItSecurity]: UserRole.it,
  [RoleTabId.AuditView]: UserRole.auditView
}

// Returns the tab ids to render. Group roles are opt-in: the single-site list
// shows seven tabs, the group screen shows all nine.
export const getRoleTabIds = (
  includeGroupRoles = false
): readonly RoleTabId[] =>
  includeGroupRoles ? GROUP_ROLE_TAB_IDS_ORDERED : ROLE_TAB_IDS

// Family role members never appear on the new Team list.
export const isListableCarer = (carer: Carer): boolean =>
  carer.role !== UserRole.family

export const carersForTab = (carers: Carer[], tab: RoleTabId): Carer[] => {
  if (tab === RoleTabId.AllMembers) {
    return carers
  }
  const role = ROLE_TAB_TO_USER_ROLE[tab]
  return carers.filter((c) => c.role === role)
}

export const isRoleTabId = (
  value: unknown,
  includeGroupRoles = false
): value is RoleTabId => {
  if (typeof value !== 'string') {
    return false
  }
  if (
    !includeGroupRoles &&
    (GROUP_ROLE_TAB_IDS as readonly string[]).includes(value)
  ) {
    return false
  }
  return (Object.values(RoleTabId) as readonly string[]).includes(value)
}
